import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, AnswerExplainer } from '../../shared/components';
import { api } from '../../utils/api';
import { noCopyHandlers } from '../../utils/noCopy';
import './Exams.css';

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function ExamRunner({ onExit }) {
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    api
      .post('/api/exams/start', {})
      .then((data) => {
        setExam(data);
        setSecondsLeft(data.durationMinutes * 60);
      })
      .catch(() => setError('Unable to start the exam right now.'));
  }, []);

  useEffect(() => {
    if (secondsLeft === null || result) return undefined;
    if (secondsLeft <= 0) {
      handleSubmit();
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft, result]);

  function selectAnswer(questionId, selectedIndex) {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedIndex }));
  }

  async function handleSubmit() {
    if (submittedRef.current || !exam) return;
    submittedRef.current = true;

    const payload = exam.questions.map((q) => ({ questionId: q.id, selectedIndex: answers[q.id] }));
    try {
      const response = await api.post(`/api/exams/${exam.examAttemptId}/submit`, { answers: payload });
      setResult(response);
    } catch (err) {
      setError('Unable to submit your exam right now.');
    }
  }

  if (error) return <p className="exams-error">{error}</p>;
  if (!exam) return <p>Preparing your exam&hellip;</p>;

  if (result) {
    const resultsById = new Map(result.results.map((r) => [r.questionId, r]));
    return (
      <div className="exams-page">
        <h1>Exam Results</h1>
        <Card className={`exams-result-card ${result.passed ? 'exams-result-card--pass' : 'exams-result-card--fail'}`}>
          <p className="exams-result-score">
            {result.correctCount} / {result.totalQuestions} ({Math.round((result.correctCount / result.totalQuestions) * 100)}%)
          </p>
          <p className="exams-result-status">{result.passed ? 'Passed' : 'Not passing yet'}</p>
        </Card>

        <h2 className="exams-section-heading">Section breakdown</h2>
        {Object.entries(result.sectionBreakdown).map(([slug, section]) => (
          <Card key={slug} className="exams-section-card">
            <span>{slug.replace(/-/g, ' ')}</span>
            <span>{section.correct} / {section.total}</span>
          </Card>
        ))}

        <h2 className="exams-section-heading">Question review</h2>
        {exam.questions.map((q, index) => {
          const r = resultsById.get(q.id);
          return (
            <Card
              key={q.id}
              className={`exam-question-review exam-no-copy ${r.correct ? 'exam-question-review--correct' : 'exam-question-review--incorrect'}`}
              {...noCopyHandlers}
            >
              <p className="exam-question-text">{index + 1}. {q.question_text}</p>
              <p className="exam-question-answer">Correct answer: {q.choices[r.correctIndex]}</p>
              {r.explanation && <p className="exam-question-explanation">{r.explanation}</p>}
              {!r.correct && (
                <AnswerExplainer
                  explanation={r.aiExplanation}
                  questionText={q.question_text}
                  choices={q.choices}
                  selectedIndex={answers[q.id]}
                  correctIndex={r.correctIndex}
                />
              )}
            </Card>
          );
        })}

        <Button onClick={onExit}>Back to exams</Button>
      </div>
    );
  }

  const allAnswered = exam.questions.every((q) => answers[q.id] !== undefined);

  return (
    <div className="exams-page">
      <div className="exams-header">
        <h1>Practice Exam</h1>
        <span className="exams-timer">{formatTime(secondsLeft)}</span>
      </div>

      {exam.questions.map((q, index) => (
        <Card key={q.id} className="exam-question-card exam-no-copy" {...noCopyHandlers}>
          <p className="exam-question-text">{index + 1}. {q.question_text}</p>
          <div className="exam-choices">
            {q.choices.map((choice, i) => (
              <label key={i} className="exam-choice">
                <input
                  type="radio"
                  name={`exam-question-${q.id}`}
                  checked={answers[q.id] === i}
                  onChange={() => selectAnswer(q.id, i)}
                />
                {choice}
              </label>
            ))}
          </div>
        </Card>
      ))}

      <div className="exams-actions">
        <Button variant="secondary" onClick={onExit}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!allAnswered}>Submit exam</Button>
      </div>
    </div>
  );
}
